/**
 * AccountSection Component
 *
 * Account management: password change, account information, delete account
 * PIPEDA Compliance - Account security and deletion
 */

'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { Mail, Lock, Calendar, CheckCircle2, XCircle, Shield, AlertTriangle, Trash2 } from 'lucide-react';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { PrimaryButton } from '@/src/shared/ui/button/PrimaryButton';
import { SecondaryButton } from '@/src/shared/ui/button/SecondaryButton';
import { Checkbox } from '@/src/shared/ui/checkbox/Checkbox';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import {
  changePassword,
  exportUserData,
  deleteAccount,
  logoutUser,
  type ChangePasswordRequest,
  type ExportUserDataResponse,
} from '../../api';
import { cn } from '@/src/shared/lib/utils';

export function AccountSection() {
  const t = useTranslations('settings.account');
  const tDelete = useTranslations('settings.deleteAccount');
  const router = useRouter();

  const [userData, setUserData] = useState<ExportUserDataResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteSection, setShowDeleteSection] = useState(false);

  // Password change form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  // Delete account form
  const [deletePassword, setDeletePassword] = useState('');
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Load user data on mount
  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const data = await exportUserData();
      setUserData(data);
    } catch (error) {
      console.error('Failed to load user data:', error);
      toast.error(t('errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!currentPassword || currentPassword.length < 8) {
      toast.error(t('password.errors.currentRequired'));
      return;
    }

    if (!newPassword || newPassword.length < 8) {
      toast.error(t('password.errors.newTooShort'));
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error(t('password.errors.mismatch'));
      return;
    }

    if (currentPassword === newPassword) {
      toast.error(t('password.errors.samePassword'));
      return;
    }

    setChangingPassword(true);
    try {
      const data: ChangePasswordRequest = {
        currentPassword,
        newPassword,
      };

      await changePassword(data);
      toast.success(t('password.success'));

      // Clear form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordForm(false);
    } catch (error: any) {
      console.error('Change password failed:', error);

      if (error.status === 401) {
        toast.error(t('password.errors.incorrectCurrent'));
      } else if (error.status === 429) {
        toast.error(t('password.errors.rateLimitExceeded'));
      } else {
        toast.error(t('password.errors.changeFailed'));
      }
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    // Validate
    if (!confirmChecked) {
      toast.error(tDelete('errors.confirmRequired'));
      return;
    }

    if (!deletePassword || deletePassword.length < 8) {
      toast.error(tDelete('errors.passwordRequired'));
      return;
    }

    setShowDeleteModal(true);
  };

  const handleFinalDelete = async () => {
    setDeleting(true);
    try {
      await deleteAccount();
      toast.success(tDelete('success.message'));

      // Logout user
      await logoutUser();

      // Redirect to home
      setTimeout(() => {
        router.push('/');
      }, 2000);
    } catch (error: any) {
      console.error('Delete account failed:', error);

      if (error.status === 429) {
        toast.error(tDelete('errors.rateLimitExceeded'));
      } else if (error.status === 401) {
        toast.error(tDelete('errors.invalidPassword'));
      } else {
        toast.error(tDelete('errors.deleteFailed'));
      }

      setShowDeleteModal(false);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return t('common.notAvailable');
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-primary" />
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="text-center py-12">
        <Typography variant="bodyMd" color="secondary">
          {t('errors.noData')}
        </Typography>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-3xl">
      {/* Page Header */}
      <div>
        <Typography variant="h2" className="mb-2">
          {t('title')}
        </Typography>
        <Typography variant="bodySm" color="secondary">
          {t('description')}
        </Typography>
      </div>

      {/* Account Information Section */}
      <section className="space-y-4">
        <Typography variant="h3">{t('info.title')}</Typography>

        <div className="space-y-3">
          {/* Email */}
          <InfoCard
            icon={Mail}
            label={t('info.email')}
            value={userData.userData.email}
            badge={
              userData.userData.isVerified ? (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-status-success/10 text-status-success text-xs">
                  <CheckCircle2 size={14} />
                  {t('info.verified')}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-status-warning/10 text-status-warning text-xs">
                  <XCircle size={14} />
                  {t('info.notVerified')}
                </span>
              )
            }
          />

          {/* Created Date */}
          <InfoCard
            icon={Calendar}
            label={t('info.createdAt')}
            value={formatDate(userData.userData.createdAt)}
          />

          {/* Last Login */}
          <InfoCard
            icon={Calendar}
            label={t('info.lastLoginAt')}
            value={formatDate(userData.userData.lastLoginAt)}
          />
        </div>
      </section>

      {/* Password Section */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <Lock className="w-5 h-5 text-text-secondary" />
          <Typography variant="h3">{t('password.title')}</Typography>
        </div>

        <Typography variant="bodySm" color="secondary" className="mb-4">
          {t('password.description')}
        </Typography>

        {!showPasswordForm ? (
          <PrimaryButton
            onClick={() => setShowPasswordForm(true)}
            className="flex items-center gap-2"
          >
            <Lock size={18} />
            {t('password.changeButton')}
          </PrimaryButton>
        ) : (
          <form onSubmit={handleChangePassword} className="space-y-4 border border-border-primary rounded-lg p-6">
            {/* Current Password */}
            <div className="space-y-2">
              <label htmlFor="currentPassword" className="block">
                <Typography variant="bodySm" weight="semibold">
                  {t('password.currentLabel')}
                </Typography>
              </label>
              <input
                id="currentPassword"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder={t('password.currentPlaceholder')}
                className="w-full px-4 py-3 border border-border-primary rounded-lg bg-background-primary text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-transparent"
                disabled={changingPassword}
                minLength={8}
                required
              />
            </div>

            {/* New Password */}
            <div className="space-y-2">
              <label htmlFor="newPassword" className="block">
                <Typography variant="bodySm" weight="semibold">
                  {t('password.newLabel')}
                </Typography>
              </label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t('password.newPlaceholder')}
                className="w-full px-4 py-3 border border-border-primary rounded-lg bg-background-primary text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-transparent"
                disabled={changingPassword}
                minLength={8}
                required
              />
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <label htmlFor="confirmPassword" className="block">
                <Typography variant="bodySm" weight="semibold">
                  {t('password.confirmLabel')}
                </Typography>
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('password.confirmPlaceholder')}
                className="w-full px-4 py-3 border border-border-primary rounded-lg bg-background-primary text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-transparent"
                disabled={changingPassword}
                minLength={8}
                required
              />
            </div>

            {/* Password Requirements */}
            <div className="bg-background-secondary rounded-lg p-3">
              <Typography variant="bodySm" color="secondary">
                {t('password.requirements')}
              </Typography>
            </div>

            {/* Form Actions */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowPasswordForm(false);
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                }}
                disabled={changingPassword}
                className="flex-1 px-4 py-3 border border-border-primary rounded-lg text-text-secondary hover:bg-background-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('password.cancel')}
              </button>
              <PrimaryButton
                type="submit"
                isLoading={changingPassword}
                className="flex-1"
              >
                {t('password.save')}
              </PrimaryButton>
            </div>
          </form>
        )}
      </section>

      {/* Security Notice */}
      <section className="bg-accent-primary/10 border border-accent-primary/20 rounded-lg p-4">
        <div className="flex gap-3">
          <Shield className="w-5 h-5 text-accent-primary flex-shrink-0 mt-0.5" />
          <div>
            <Typography variant="bodyMd" weight="semibold" className="mb-1">
              {t('security.title')}
            </Typography>
            <Typography variant="bodySm" color="secondary">
              {t('security.description')}
            </Typography>
          </div>
        </div>
      </section>

      {/* Delete Account Section */}
      <section className="space-y-4 border-t border-border-primary pt-8">
        <button
          onClick={() => setShowDeleteSection(!showDeleteSection)}
          className="flex items-center gap-2 text-status-error hover:text-status-error/80 transition-colors"
        >
          <Trash2 size={20} />
          <Typography variant="h3" className="text-status-error">
            {tDelete('title')}
          </Typography>
        </button>

        {showDeleteSection && (
          <div className="space-y-6">
            {/* Warning Banner */}
            <div className="bg-status-error/10 border border-status-error/30 rounded-lg p-4">
              <div className="flex gap-3">
                <AlertTriangle className="w-6 h-6 text-status-error flex-shrink-0 mt-0.5" />
                <div>
                  <Typography variant="bodyMd" weight="semibold" className="mb-2 text-status-error">
                    {tDelete('warning.title')}
                  </Typography>
                  <Typography variant="bodySm" color="secondary">
                    {tDelete('warning.message')}
                  </Typography>
                </div>
              </div>
            </div>

            {/* Confirmation Form */}
            <div className="space-y-4 border border-border-primary rounded-lg p-6">
              {/* Confirmation Checkbox */}
              <div className="flex items-start gap-3">
                <Checkbox
                  checked={confirmChecked}
                  onChange={(e) => setConfirmChecked(e.target.checked)}
                  className="mt-1"
                />
                <label className="flex-1 cursor-pointer" onClick={() => setConfirmChecked(!confirmChecked)}>
                  <Typography variant="bodyMd">
                    {tDelete('form.confirmCheckbox')}
                  </Typography>
                </label>
              </div>

              {/* Password Input */}
              <div className="space-y-2">
                <label htmlFor="deletePassword" className="block">
                  <Typography variant="bodySm" weight="semibold">
                    {tDelete('form.passwordLabel')}
                  </Typography>
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-tertiary" />
                  <input
                    id="deletePassword"
                    type="password"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    placeholder={tDelete('form.passwordPlaceholder')}
                    className="w-full pl-10 pr-4 py-3 border border-border-primary rounded-lg bg-background-primary text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-accent-primary focus:border-transparent"
                    disabled={deleting}
                  />
                </div>
                <Typography variant="bodySm" color="secondary">
                  {tDelete('form.passwordHelp')}
                </Typography>
              </div>

              {/* Delete Button */}
              <PrimaryButton
                onClick={handleDeleteAccount}
                disabled={!confirmChecked || !deletePassword || deleting}
                className="w-full bg-status-error hover:bg-status-error/90 flex items-center justify-center gap-2"
              >
                <Trash2 size={18} />
                {tDelete('form.deleteButton')}
              </PrimaryButton>
            </div>
          </div>
        )}
      </section>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
          <div className="bg-surface-primary rounded-lg shadow-xl max-w-md w-full p-6 space-y-4 animate-slide-up">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-status-error/10 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-status-error" />
              </div>
              <Typography variant="h3" className="text-status-error">
                {tDelete('modal.title')}
              </Typography>
            </div>

            <Typography variant="bodyMd">
              {tDelete('modal.message')}
            </Typography>

            <Typography variant="bodySm" color="secondary" className="bg-background-secondary p-3 rounded-lg">
              {tDelete('modal.finalWarning')}
            </Typography>

            <div className="flex gap-3 pt-2">
              <SecondaryButton
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className="flex-1"
              >
                {tDelete('modal.cancelButton')}
              </SecondaryButton>
              <PrimaryButton
                onClick={handleFinalDelete}
                isLoading={deleting}
                className="flex-1 bg-status-error hover:bg-status-error/90"
              >
                {tDelete('modal.confirmButton')}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Helper component for info cards
function InfoCard({
  icon: Icon,
  label,
  value,
  badge,
}: {
  icon: typeof Mail;
  label: string;
  value: string;
  badge?: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-4 p-4 border border-border-primary rounded-lg bg-background-secondary">
      <div className="w-10 h-10 rounded-lg bg-accent-primary/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-accent-primary" />
      </div>
      <div className="flex-1 min-w-0">
        <Typography variant="bodySm" color="secondary" className="mb-0.5">
          {label}
        </Typography>
        <div className="flex items-center gap-2 flex-wrap">
          <Typography variant="bodyMd" weight="semibold" className="truncate">
            {value}
          </Typography>
          {badge}
        </div>
      </div>
    </div>
  );
}
